import { Build, Component, ComponentInterface, Host, JSX, Method, Prop, State, h } from '@stencil/core';
import { ProductCategory as ProductCategoryEnum } from '@aglenergy/shared-library/dist/generated-source/api/sales/models/ProductCategory';
import { SessionStorageKeys } from '@aglenergy/shared-library';
import {
  AddressDetailModel,
  ContentMode,
  Context,
  DataSpaKey,
  ProductCatalogueProduct,
  ProductCategory,
  ProductSessionData,
  ProductsResponse,
  PromoDialogData,
  ReportErrorType
} from 'app/shared/models/index';
import {
  bundleService,
  configService,
  couponCodeService,
  crossSellService,
  errorReportingService,
  featureService,
  productsSessionStorageService,
  spinnerService,
  tealiumAnalyticService
} from 'app/shared/services/index';
import { renderTechTypeMessage, showNbnSqMessage } from 'app/nbn/components/nbn-technology-type-message/nbn-technology-type-message';

import { DataSpaSearchParams } from 'app/shared/utilities/data-spa-search-params.util';
import { FeatureKey } from 'app/shared/models/feature-key';
import { NbnProductsResponse } from 'app/nbn/models/nbn-products-response.model';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { getDataSpaSearchParams } from 'app/shared/utilities/helper-functions';
import { initStore } from 'app/shared/store/init-store';
import { isNotNumber } from 'utils/dom.helper';
import { changePlanService as mobileChangePlanService } from 'app/mobile/services/change-plan/change-plan.service';
import { changePlanService as nbnChangePlanService } from 'app/nbn/services/change-plan/change-plan.service';
import { parseProductCategory } from 'app/shared/utilities/product-category.util';
import { store } from 'app/shared/store/store';

@Component({
  tag: 'app-product-cards',
  styleUrl: 'product-cards.scss'
})
export class ProductCards implements ComponentInterface {
  @Prop() public readonly dataSpa: string = null;

  private spaParams: DataSpaSearchParams;
  private isSupportedProductCategory: boolean;
  private mediaQueryList: MediaQueryList = window.matchMedia('(min-width: 720px)');
  @State() private isGuidedFlow = false;
  @State() private canRenderPlans: boolean;
  @State() private nbnMoversCrossSell = false;
  @State() private productCategory: ProductCategory;
  @State() private isChangePlanPage = false;
  @State() private eligibilityCodeModal: PromoDialogData;
  private products: ProductCatalogueProduct[];

  public async componentWillLoad(): Promise<void> {
    crossSellService.removeCrossSellProductCategory();
    let couponCode = couponCodeService.getFromQueryString();
    if (this.dataSpa) {
      this.spaParams = getDataSpaSearchParams(this.dataSpa);
      const mode = this.spaParams.get(DataSpaKey.mode);
      let host = '';
      if (mode === 'publish') {
        host = '/apps/comparison';
      } else {
        host = this.spaParams.get(DataSpaKey.host);
      }
      this.productCategory = this.spaParams.get(DataSpaKey.productcategory) as ProductCategory;
      this.nbnMoversCrossSell = this.spaParams.get(DataSpaKey.nbnMoversCrossSell) === 'true';
      const contentMode = this.spaParams.get(DataSpaKey.mode) as ContentMode;
      store.set('contentMode', contentMode ?? 'publish'); // setting this earlier before initStore as configService.loadConfig needs it for the general content call
      const focusSku = this.spaParams.get(DataSpaKey.focusSku);
      const crossSell = this.spaParams.get(DataSpaKey.crossSell);
      const bundleProducts = new URLSearchParams(window.location.search).get('bundle');
      const isCampaignPage = this.spaParams.getBoolean(DataSpaKey.isCampaignPage);
      store.set(StoreKeys.isCampaignPage, isCampaignPage);
      const webIDForCampaign = new URLSearchParams(window.location.search.toUpperCase()).get('WEBID');

      const previousWebId = productsSessionStorageService.getLatestWebIdFromProducts();
      if (isCampaignPage) {
        store.set(StoreKeys.webId, webIDForCampaign);

        if (previousWebId?.toUpperCase() !== webIDForCampaign?.toUpperCase()) {
          productsSessionStorageService.removeProducts();
        }
      } else if (previousWebId) {
        productsSessionStorageService.removeProducts();
      }

      this.isGuidedFlow = !!this.spaParams.get(DataSpaKey.isGuidedFlow);
      // Get couponCode from data-spa if no couponCode in query string
      if (!this.isGuidedFlow) {
        if (!couponCode) {
          couponCode = this.spaParams.get(DataSpaKey.couponCode);
        }
        store.set('couponCode', couponCode);
      }
      store.set('focusSku', focusSku);
      store.set('couponCode', couponCode);

      this.validateProductCategory(this.productCategory);

      if (bundleProducts) {
        bundleProducts.split('-').forEach((product) => {
          bundleService.setBundleConfigProduct(product as ProductCategory, { displayBundle: true });
        });
        bundleService.setBundleConfigProduct(this.productCategory, { displayBundle: false }); // already in product category, no need to display on bridge page
      }

      await configService.loadConfig(host).then(async () => {
        if (!this.isGuidedFlow) {
          await initStore(false, store.get('isGuidedFlow'), this.productCategory, contentMode, focusSku, host);
        }
      });

      const isBundleFlowEnabled = await featureService.loadFeatureStatus(FeatureKey.BUNDLE_FLOW_ENABLED);
      if (isBundleFlowEnabled && bundleService.isExplicitBundleFlow()) {
        this.hideBannerAndFeature();
      }

      const crossSellProductCategory = parseProductCategory(crossSell);
      if (crossSellProductCategory) {
        sessionStorage.setItem(SessionStorageKeys.CrossSellProductCategory, crossSell);
      }
    } else {
      store.set('couponCode', couponCode);
    }

    // Run it initially
    this.handleDeviceChange();
    this.mediaQueryList.addEventListener
      ? this.mediaQueryList.addEventListener('change', this.handleDeviceChange)
      : this.mediaQueryList.addListener(this.handleDeviceChange);

    store.onChange(StoreKeys.productDetails, () => {
      this.checkEligibilityCodeModal();
    });
    this.checkEligibilityCodeModal();
    store.onChange(StoreKeys.isPromoModalOpen, () => {
      if (store.get(StoreKeys.isPromoModalOpen)) {
        this.checkEligibilityCodeModal();
      }
    });
    spinnerService.removePlansSkeletonLoader();
  }

  public hideBannerAndFeature(): void {
    const headers = document.getElementsByClassName('headlinebanner');
    if (headers && headers.length > 0) {
      const header = headers[0] as HTMLElement;
      header.style.display = 'none';
    }
    const featureItems = document.getElementsByClassName('icontextcard');
    if (featureItems && featureItems.length > 0) {
      const featureParent = (featureItems[0] as HTMLElement).parentElement;
      featureParent.style.display = 'none';
    }
  }

  public disconnectedCallback(): void {
    this.mediaQueryList.removeEventListener
      ? this.mediaQueryList.removeEventListener('change', this.handleDeviceChange)
      : this.mediaQueryList.removeListener(this.handleDeviceChange);
  }

  private validateProductCategory(productCategory: ProductCategory): void {
    const supportedProductCategories: ProductCategory[] = ['mobile plan', 'nbn'];
    this.isSupportedProductCategory = supportedProductCategories.includes(productCategory);
    if (!this.isSupportedProductCategory) {
      const errorMessage = 'Missing or Invalid productCategory defined in data-spa attribute';
      console.error(errorMessage);
      errorReportingService.logError(errorMessage, ReportErrorType.ConfigurationError);
    }
  }

  private checkEligibilityCodeModal() {
    const productDetails = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
    if (productDetails) this.eligibilityCodeModal = productDetails.length > 0 ? productDetails[0]?.response?.eligibilityCodeModal : null;
  }

  private handleDeviceChange = () => {
    this.equaliseHeights(true);
    this.equaliseHeights();
  };

  // eslint-disable-next-line require-await
  @Method()
  public async equaliseHeights(setToAuto = false): Promise<void> {
    this.equaliseSectionHeights('.feature-list', setToAuto);
    this.equaliseSectionHeights('.bundle-price-desc', setToAuto);
    this.equaliseSectionHeights('.links', setToAuto);
  }
  private equaliseSectionHeights(selector: string, setToAuto = false): void {
    // Get all instances of the card section with specified selector
    const elementArray = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
    if (!elementArray || elementArray.length === 0) {
      return;
    }

    for (let i = 0; i < elementArray.length; i++) {
      const element = elementArray[i];
      element.style.height = `auto`;
    }
    if (setToAuto) {
      return;
    }

    let maxElementHeight = 0;
    for (let i = 0; i < elementArray.length; i++) {
      const clientHeight = elementArray[i].clientHeight;
      maxElementHeight = clientHeight > maxElementHeight ? clientHeight : maxElementHeight;
    }

    // apply that height to all elements
    if (maxElementHeight > 0) {
      for (let i = 0; i < elementArray.length; i++) {
        const element = elementArray[i];
        element.style.height = `${maxElementHeight}px`;
      }
    }
  }

  public getFocusCardIndex(focusSku: string, products: ProductCatalogueProduct[]): number {
    const defaultIndex = products.findIndex((p) => p.sku === focusSku);
    return defaultIndex > -1 ? defaultIndex : 0;
  }

  public filterProducts(productDetails: ProductsResponse, selectedAddress: AddressDetailModel): ProductCatalogueProduct[] {
    if (!productDetails || !productDetails.products || !productDetails.products.length) {
      return [];
    }
    let products = productDetails.products.filter((p) => !!p.content && !isNotNumber(p.attributes.agl_marketing_price));

    if (this.spaParams && productDetails && products.length > 0) {
      const productSku = this.spaParams.get('productSku');
      const skuList: string[] = productSku && productSku.split(',');

      if (skuList && skuList.length > 0) {
        products = products.filter((p) => skuList.includes(p.sku));
      }
      if (!selectedAddress) {
        tealiumAnalyticService.trackProductCards(products, true, store.get(StoreKeys.productCategory));
      }
    }
    const selectedCategory = store.get(StoreKeys.selectedPlanCategory);
    if (selectedCategory) {
      products = products.filter((p) => p.content.planCategories && p.content.planCategories.includes(selectedCategory));
    }
    return products;
  }

  private getPlanCategories(productDetails: ProductsResponse): string[] {
    if (!productDetails || productDetails.planCategories?.length === 0) {
      return [];
    }
    return productDetails.planCategories;
  }

  private didRenderFunc = () => {
    this.equaliseHeights();
  };

  private checkRenderPlans = (productDetails: ProductsResponse): boolean => {
    const prefillAddress = store.get(StoreKeys.prefillAddress);
    const isDefaultProductSearch = !(productDetails as NbnProductsResponse)?.serviceQualification;
    return !prefillAddress || !isDefaultProductSearch;
  };

  private renderFunc = ({ productDetails, selectedAddress, isPersonalisedProductsLoading, isPageLoaded, responseError }: Context) => {
    const products = this.filterProducts(productDetails, selectedAddress);
    this.products = products;

    const productCategory = store.get(StoreKeys.productCategory);
    const focusSku = store.get('focusSku');
    const focusCardIndex = this.getFocusCardIndex(focusSku, products);
    spinnerService.removeSpinnerOverlay(isPageLoaded);
    if (!this.isSupportedProductCategory) {
      return <error-boundary />;
    }

    if (isPageLoaded) {
      if (!products?.length && (!responseError || responseError?.errorType !== 'aborted') && focusSku) {
        return <error-boundary />;
      }
      if (productCategory === 'nbn') {
        this.canRenderPlans = this.checkRenderPlans(productDetails);

        if (!this.canRenderPlans) {
          return;
        }

        const isChangePlan = nbnChangePlanService.isChangePlan();
        if (isChangePlan) {
          const categories = this.getPlanCategories(store.get(StoreKeys.productDetails));
          this.isChangePlanPage = true;
          const productsForDisplay = nbnChangePlanService.getChangePlanProductsForDisplay(productDetails.products, products);
          return (
            <nbn-change-plan-product-card
              products={productsForDisplay}
              focusCardIndex={1}
              isPersonalisedProductsLoading={isPersonalisedProductsLoading}
              selectedAddress={selectedAddress}
              productCategories={categories}
            />
          );
        }

        return (
          <nbn-product-card
            products={products}
            selectedAddress={selectedAddress}
            focusCardIndex={focusCardIndex}
            isPersonalisedProductsLoading={isPersonalisedProductsLoading}
          />
        );
      }
      if (productCategory === 'mobile plan') {
        this.canRenderPlans = true;

        const isChangePlan = mobileChangePlanService.isChangePlan();
        if (isChangePlan) {
          this.isChangePlanPage = true;
          const productsForDisplay = mobileChangePlanService.getChangePlanProductsForDisplay(productDetails.products, products);
          return (
            <mobile-change-plan-product-card
              products={productsForDisplay}
              focusCardIndex={1}
              isPersonalisedProductsLoading={isPersonalisedProductsLoading}
            />
          );
        }
        return (
          <div>
            <mobile-product-card
              products={products}
              focusCardIndex={focusCardIndex}
              isPersonalisedProductsLoading={isPersonalisedProductsLoading}
            />
          </div>
        );
      }
    }
  };

  private renderNbnMoversCrossSellFibreSubHeading = () => {
    const featureFlag = featureService.getFeatureStatus(FeatureKey.NBN_MOVERS_CROSS_SELL_ENABLED);
    if (featureFlag && this.productCategory === ProductCategoryEnum.Nbn && this.nbnMoversCrossSell) {
      const productSessionData = productsSessionStorageService.getLatestProductsByProductCategory('nbn') as ProductSessionData;
      const productDetails = store.get(StoreKeys.productDetails);
      return showNbnSqMessage(productDetails as NbnProductsResponse, productSessionData?.key, false, this.nbnMoversCrossSell);
    }
  };

  private renderGuidedFlowHeading = () => {
    const productCategory = store.get(StoreKeys.productCategory);
    if (productCategory === 'nbn') {
      const nbnProductDetails = store.get(StoreKeys.productDetails) as NbnProductsResponse;
      const address = store.get('address') as string;
      const delayMessage = store.get('delayMessage');
      return (
        <div class="guided-flow-nbn-heading">
          {renderTechTypeMessage(nbnProductDetails, address, false, delayMessage, false, null, true)}
        </div>
      );
    } else if (productCategory === 'mobile plan') {
      return (
        <div class="guided-flow__product-heading">
          <agl-ds-p styledAs="inherit">Choose a mobile plan to add to your combined services</agl-ds-p>
        </div>
      );
    }
  };

  private renderPlanCategories = () => {
    const categories = this.getPlanCategories(store.get(StoreKeys.productDetails));
    return <plan-categories planCategories={categories}> </plan-categories>;
  };

  private renderPromoDialog = () => {
    return <promo-dialog products={this.products} promoDialogData={this.eligibilityCodeModal} />;
  };

  public logVersion(): void {
    if (!Build.isDev) {
      const style = 'color: green; background: yellow;';
      const version = `Compare Product SPA |buildNumber: 20250501.2|buildDate: 2025-05-01 13:04:28+10:00`;
      setTimeout(() => console.log('%c%s', style, version), 2000);
    }
  }

  public render(): JSX.Element {
    return (
      <Host>
        <dls-theme-agl> </dls-theme-agl>
        {this.isGuidedFlow ? this.renderGuidedFlowHeading() : null}
        {this.renderNbnMoversCrossSellFibreSubHeading()}
        <nbn-fibre-upgrade-message />
        {this.canRenderPlans && this.renderPlanCategories()}
        <div
          class={`comparison-section-wrapper${this.isChangePlanPage || store.get(StoreKeys.isCampaignPage) ? ' trim-space' : ''}`}
          id="panning-wrapper"
        >
          <product-comparison-container didRenderFunc={this.didRenderFunc} renderFunc={this.renderFunc} />
        </div>
        {this.eligibilityCodeModal && (
          <promo-dialog-dls
            products={this.products}
            promoDialogData={this.eligibilityCodeModal}
            isOpen={store.get(StoreKeys.isPromoModalDlsOpen)}
          />
        )}
        {store.get(StoreKeys.isPromoModalOpen) && <div class="promo-dialog-container">{this.renderPromoDialog()}</div>}
      </Host>
    );
  }
}
